import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  updateProfile,
  updatePassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  deleteUser
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  deleteDoc
} from 'firebase/firestore';
import { auth, db, logAuthEvent, logProfileEvent, logErrorEvent } from './firebase';
import toast from 'react-hot-toast';

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Auth state listener
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // Ensure user document exists in Firestore whenever auth state changes
      try {
        await createUserDocument(user);
      } catch (error) {
        console.error('Error ensuring user document exists:', error);
      }
    } else {
      // Log auth state change to logged out
      await logAuthEvent('auth_state_changed', 'User auth state changed to logged out');
    }
    callback(user);
  });
};

// Sign in with Google
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Create or update user document in Firestore
    await createUserDocument(user);
    
    toast.success(`Welcome, ${user.displayName}!`);
    return { success: true, user };
  } catch (error) {
    console.error('Google sign-in error:', error);
    toast.error('Failed to sign in with Google');
    return { success: false, error: error.message };
  }
};

// Sign up with email and password
export const signUpWithEmail = async (email, password, displayName) => {
  try {
    console.log('🔧 Starting account creation process...');
    console.log('📧 Email:', email);
    console.log('👤 Display Name:', displayName);
    console.log('🔐 Password length:', password?.length);
    
    const result = await createUserWithEmailAndPassword(auth, email, password);
    console.log('✅ Firebase user created successfully:', result.user.uid);
    
    const user = result.user;
    
    // Update user profile with display name
    console.log('🔄 Updating user profile with display name...');
    await updateProfile(user, { displayName });
    console.log('✅ User profile updated successfully');
    
    // Send email verification
    console.log('📧 Sending email verification...');
    await sendEmailVerification(user);
    console.log('✅ Email verification sent successfully');
    
    // Create user document in Firestore
    console.log('💾 Creating user document in Firestore...');
    await createUserDocument(user);
    console.log('✅ User document created successfully');
    
    toast.success('Account created! Please check your email for verification.');
    return { success: true, user };
  } catch (error) {
    console.error('❌ Email sign-up error:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    
    let errorMessage = 'Failed to create account';
    
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'Email is already registered';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password should be at least 6 characters';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    }
    
    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  }
};

// Sign in with email and password
export const signInWithEmail = async (email, password) => {
  try {
    console.log('🔐 Starting user login...');
    
    // Log login attempt
    await logAuthEvent('login_attempt', `Login attempt for email: ${email}`, null, email);
    
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;
    
    if (!user.emailVerified) {
      toast.error('Please verify your email before signing in');
      await signOut(auth);
      return { success: false, error: 'Email not verified' };
    }
    
    // Update last login
    await updateUserDocument(user.uid, { lastLogin: new Date() });
    
    toast.success(`Welcome back, ${user.displayName || user.email}!`);
    
    // Log successful login
    await logAuthEvent('user_login', `User successfully logged in: ${email}`, user.uid, email);
    
    return { success: true, user };
  } catch (error) {
    console.error('Email sign-in error:', error);
    let errorMessage = 'Failed to sign in';
    
    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Incorrect password';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    }
    
    toast.error(errorMessage);
    
    // Log login failure
    await logErrorEvent(error, 'authService.js:signInWithEmail', { email });
    await logAuthEvent('login_failed', `Login failed for email: ${email} - ${error.message}`, null, email);
    
    return { success: false, error: errorMessage };
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    await signOut(auth);
    toast.success('Signed out successfully');
    return { success: true };
  } catch (error) {
    console.error('Sign-out error:', error);
    toast.error('Failed to sign out');
    return { success: false, error: error.message };
  }
};

// Update user profile
export const updateUserProfile = async (userId, profileData) => {
  try {
    console.log('👤 Updating user profile...');
    
    const user = auth.currentUser;
    const userEmail = user?.email;
    
    // Log profile update attempt
    await logProfileEvent('profile_update_attempt', `Profile update attempt for user: ${userEmail}`, userId, profileData);
    
    // Get current profile data for comparison
    const userRef = doc(db, 'users', userId);
    const currentDoc = await getDoc(userRef);
    const currentData = currentDoc.exists() ? currentDoc.data() : {};
    
    // Update Firestore document
    const updateData = {
      ...profileData,
      updatedAt: new Date()
    };
    
    await updateDoc(userRef, updateData);
    
    // Update Firebase Auth profile if displayName changed
    if (profileData.displayName && user) {
      await updateProfile(user, { displayName: profileData.displayName });
    }
    
    // Log successful profile update with changes
    const changes = getObjectDifferences(currentData, { ...currentData, ...updateData });
    await logProfileEvent('profile_updated', `Profile updated for user: ${userEmail}`, userId, { changes, newData: profileData });
    
    console.log('✅ Profile updated successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Profile update error:', error);
    
    // Log profile update failure
    await logErrorEvent(error, 'authService.js:updateUserProfile', { userId, profileData });
    await logProfileEvent('profile_update_failed', `Profile update failed for user: ${userId} - ${error.message}`, userId);
    
    return { success: false, error: error.message };
  }
};

// Change user password
export const changeUserPassword = async (newPassword) => {
  try {
    console.log('🔐 Changing user password...');
    
    const user = auth.currentUser;
    const userEmail = user?.email;
    const userId = user?.uid;
    
    // Log password change attempt
    await logAuthEvent('password_change_attempt', `Password change attempt for user: ${userEmail}`, userId, userEmail);
    
    await updatePassword(user, newPassword);
    
    // Update password change timestamp
    if (userId) {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        passwordChangedAt: new Date() 
      });
    }
    
    // Log successful password change
    await logAuthEvent('password_changed', `Password successfully changed for user: ${userEmail}`, userId, userEmail);
    
    console.log('✅ Password changed successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Password change error:', error);
    
    // Log password change failure
    await logErrorEvent(error, 'authService.js:changeUserPassword');
    await logAuthEvent('password_change_failed', `Password change failed - ${error.message}`);
    
    return { success: false, error: error.message };
  }
};

// Enhanced password reset with email delivery verification
export const sendUserPasswordResetEmailEnhanced = async (email) => {
  try {
    console.log('🔧 Enhanced password reset process starting for:', email);
    
    // Step 1: Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }
    
    // Step 2: Check Firebase Auth configuration
    if (!auth) {
      console.error('❌ Firebase Auth not initialized');
      throw new Error('Authentication service not available');
    }
    
    console.log('✅ Firebase Auth is initialized');
    console.log('🔧 Auth domain:', auth.config?.authDomain);
    console.log('🔧 Project ID:', auth.config?.projectId);
    
    // Step 3: Check if we're in development mode
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname.includes('localhost');
    
    console.log('🔧 Environment:', isDevelopment ? 'Development' : 'Production');
    console.log('🔧 Current domain:', window.location.hostname);
    
    // Step 4: Attempt to send password reset email
    console.log('📧 Attempting to send password reset email via Firebase Auth...');
    
    try {
      // Configure action code settings for better email delivery
      const actionCodeSettings = {
        url: window.location.origin,
        handleCodeInApp: false
      };
      
      console.log('🔧 Action code settings:', actionCodeSettings);
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      
      console.log('✅ Firebase Auth sendPasswordResetEmail completed successfully');
      
      // Important: Firebase doesn't provide delivery confirmation
      // The success only means the request was processed, not that email was delivered
      
      const successMessage = isDevelopment 
        ? `Password reset request sent for ${email}. Note: In development, emails may not be delivered. Check Firebase Console for email configuration.`
        : `Password reset instructions have been sent to ${email}. Please check your email (including spam folder).`;
      
      toast.success(successMessage, { duration: 8000 });
      
      // Provide additional guidance
      setTimeout(() => {
        toast.info(
          'If you don\'t receive the email within 5 minutes, please:\n' +
          '1. Check your spam/junk folder\n' +
          '2. Verify the email address is correct\n' +
          '3. Contact support if the issue persists',
          { duration: 10000 }
        );
      }, 2000);
      
      // Log successful password reset email
      await logAuthEvent('password_reset_email_sent', `Password reset email sent to: ${email}`, null, email);
      
      return { 
        success: true, 
        message: successMessage,
        emailDeliveryNote: 'Email delivery depends on Firebase project configuration'
      };
      
    } catch (authError) {
      console.error('❌ Firebase Auth error:', authError);
      console.error('❌ Error code:', authError.code);
      console.error('❌ Error message:', authError.message);
      
      // Handle specific Firebase Auth errors
      let errorMessage = 'Failed to send password reset email';
      
      switch (authError.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address. Please check the email or create a new account.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many reset attempts. Please wait 15-30 minutes before trying again.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection and try again.';
          break;
        case 'auth/unauthorized-domain':
          errorMessage = 'This domain is not authorized for password reset. Please contact support.';
          break;
        case 'auth/invalid-action-code':
          errorMessage = 'Invalid or expired reset code';
          break;
        case 'auth/quota-exceeded':
          errorMessage = 'Email quota exceeded. Please try again later or contact support.';
          break;
        default:
          errorMessage = `Reset failed: ${authError.message}`;
      }
      
      throw new Error(errorMessage);
    }
    
  } catch (error) {
    console.error('❌ Enhanced password reset error:', error);
    
    let finalErrorMessage = 'Failed to send password reset email';
    
    if (error.message.includes('Invalid email format')) {
      finalErrorMessage = 'Please enter a valid email address';
    } else if (error.message.includes('Authentication service not available')) {
      finalErrorMessage = 'Service temporarily unavailable. Please try again later';
    } else if (error.message) {
      finalErrorMessage = error.message;
    }
    
    toast.error(finalErrorMessage);
    return { success: false, error: finalErrorMessage };
  }
};

// Send password reset email (uses enhanced version)
export const sendUserPasswordResetEmail = async (email) => {
  return await sendUserPasswordResetEmailEnhanced(email);
};

// Alternative password reset function (fallback)
export const sendUserPasswordResetEmailSimple = async (email) => {
  try {
    console.log('🔧 Using simple password reset for:', email);
    
    // Direct Firebase Auth call without additional checks
    await sendPasswordResetEmail(auth, email);
    
    console.log('✅ Simple password reset email sent');
    toast.success('Password reset instructions have been sent to your email');
    
    return { 
      success: true, 
      message: 'Password reset instructions have been sent to your email' 
    };
    
  } catch (error) {
    console.error('❌ Simple password reset error:', error);
    
    let errorMessage = 'Failed to send password reset email';
    
    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email address';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many reset attempts. Please try again later';
    }
    
    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  }
};

// Function to check Firebase email configuration
export const checkFirebaseEmailConfig = async () => {
  try {
    console.log('🔧 Checking Firebase email configuration...');
    
    const configInfo = {
      authDomain: auth?.config?.authDomain,
      projectId: auth?.config?.projectId,
      apiKey: auth?.config?.apiKey ? 'Present' : 'Missing',
      currentDomain: window.location.hostname,
      environment: window.location.hostname === 'localhost' ? 'Development' : 'Production'
    };
    
    console.log('📋 Firebase Configuration:', configInfo);
    
    // Check if we can access Firebase project settings
    const testResult = {
      authInitialized: !!auth,
      configComplete: !!(auth?.config?.authDomain && auth?.config?.projectId),
      domainMatch: window.location.hostname === 'localhost' || 
                   auth?.config?.authDomain?.includes(window.location.hostname),
      recommendations: []
    };
    
    if (!testResult.authInitialized) {
      testResult.recommendations.push('Firebase Auth is not initialized');
    }
    
    if (!testResult.configComplete) {
      testResult.recommendations.push('Firebase configuration is incomplete');
    }
    
    if (!testResult.domainMatch && window.location.hostname !== 'localhost') {
      testResult.recommendations.push('Current domain may not be authorized in Firebase Console');
    }
    
    if (window.location.hostname === 'localhost') {
      testResult.recommendations.push('Development mode: Emails may not be delivered without proper SMTP configuration');
    }
    
    console.log('📊 Configuration Test Results:', testResult);
    
    return testResult;
    
  } catch (error) {
    console.error('❌ Error checking Firebase config:', error);
    return { 
      success: false, 
      error: error.message,
      recommendations: ['Unable to check Firebase configuration']
    };
  }
};

// Debug function to check Firebase Auth status
export const debugFirebaseAuth = () => {
  console.log('🔧 Firebase Auth Debug Info:');
  console.log('- Auth instance:', auth);
  console.log('- Auth config:', auth?.config);
  console.log('- Auth domain:', auth?.config?.authDomain);
  console.log('- Current user:', auth?.currentUser);
  console.log('- App instance:', auth?.app);
  
  return {
    authInitialized: !!auth,
    authDomain: auth?.config?.authDomain,
    currentUser: auth?.currentUser?.email || null,
    appName: auth?.app?.name
  };
};

// Verify password reset code (for custom reset flows)
export const verifyPasswordResetCode = async (code) => {
  try {
    // This would be used if implementing a custom password reset flow
    // For now, we're using Firebase's built-in reset flow
    return { success: true };
  } catch (error) {
    console.error('Error verifying password reset code:', error);
    return { success: false, error: error.message };
  }
};

// Confirm password reset (for custom reset flows)
export const confirmPasswordReset = async (code, newPassword) => {
  try {
    // This would be used if implementing a custom password reset flow
    // For now, we're using Firebase's built-in reset flow
    return { success: true };
  } catch (error) {
    console.error('Error confirming password reset:', error);
    return { success: false, error: error.message };
  }
};

// Create user document in Firestore
export const createUserDocument = async (user) => {
  try {
    console.log('🔧 Creating user document for:', user.uid, user.email);
    
    const userRef = doc(db, 'users', user.uid);
    
    try {
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        console.log('📝 Creating new user document...');
        
        // Extract provider information
        let provider = 'email';
        if (user.providerData && user.providerData.length > 0) {
          provider = user.providerData[0].providerId;
        }
        
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          emailVerified: user.emailVerified || false,
          provider: provider,
          createdAt: new Date(),
          lastLogin: new Date(),
          chatCount: 0,
          company: '',
          phone: '',
          location: '',
          designation: 'Other',
          updatedAt: new Date()
        };
        
        await setDoc(userRef, userData);
        console.log('✅ New user document created successfully');
        
        return { success: true, userData };
      } else {
        console.log('🔄 Updating existing user document...');
        // Update existing user with setDoc merge to avoid permission issues
        const updateData = {
          lastLogin: new Date(),
          emailVerified: user.emailVerified,
          displayName: user.displayName || userSnap.data().displayName,
          photoURL: user.photoURL || userSnap.data().photoURL,
          updatedAt: new Date()
        };
        
        await setDoc(userRef, updateData, { merge: true });
        console.log('✅ Existing user document updated successfully');
        return { success: true, userData: { ...userSnap.data(), ...updateData } };
      }
    } catch (permissionError) {
      console.error('❌ Permission error in createUserDocument:', permissionError);
      
      // Try with basic setDoc if getDoc fails due to permissions
      console.log('🔧 Attempting direct document creation due to permission error...');
      
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        emailVerified: user.emailVerified || false,
        provider: user.providerData?.[0]?.providerId || 'email',
        createdAt: new Date(),
        lastLogin: new Date(),
        chatCount: 0,
        company: '',
        phone: '',
        location: '',
        designation: 'Other',
        updatedAt: new Date()
      };
      
      await setDoc(userRef, userData, { merge: true });
      console.log('✅ User document created with direct setDoc');
      return { success: true, userData };
    }
  } catch (error) {
    console.error('❌ Error creating user document:', error);
    console.error('❌ Error details:', error.message);
    return { success: false, error: error.message };
  }
};

// Update user document
export const updateUserDocument = async (uid, data) => {
  try {
    console.log('🔄 Updating user document for:', uid, 'with data:', data);
    
    // Verify user is authenticated and matches the uid
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    if (currentUser.uid !== uid) {
      throw new Error('Unauthorized: Cannot update another user\'s document');
    }
    
    const userRef = doc(db, 'users', uid);
    
    // Get existing document data first
    const userSnap = await getDoc(userRef);
    let existingData = {};
    
    if (userSnap.exists()) {
      existingData = userSnap.data();
      console.log('📄 Found existing user document');
    } else {
      console.log('⚠️ User document does not exist, will create with provided data');
      // Create basic user data structure
      existingData = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || '',
        photoURL: currentUser.photoURL || '',
        emailVerified: currentUser.emailVerified || false,
        provider: currentUser.providerData?.[0]?.providerId || 'email',
        createdAt: new Date(),
        chatCount: 0
      };
    }
    
    // Merge existing data with new data
    const updatedData = {
      ...existingData,
      ...data,
      updatedAt: new Date(),
      lastLogin: new Date()
    };
    
    // Use setDoc with merge to avoid permission issues
    await setDoc(userRef, updatedData, { merge: true });
    
    console.log('✅ User document updated successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error updating user document:', error);
    console.error('❌ Error details:', error.message);
    
    // If it's a permission error, try a different approach
    if (error.code === 'permission-denied') {
      try {
        console.log('🔄 Permission denied, trying alternative approach...');
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Force recreate the user document
          await createUserDocument(currentUser);
          console.log('✅ User document recreated successfully');
          return { success: true };
        }
      } catch (retryError) {
        console.error('❌ Retry failed:', retryError);
        return { success: false, error: 'Permission denied. Please sign out and sign in again.' };
      }
    }
    
    return { success: false, error: error.message };
  }
};

// Get user chat history
export const getUserChatHistory = async (userId) => {
  try {
    console.log('🔍 Loading chat history for user:', userId);
    
    // Try with orderBy first, fallback to simple query if it fails
    let snapshot;
    try {
      const chatsQuery = query(
        collection(db, 'chats'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      snapshot = await getDocs(chatsQuery);
    } catch (orderError) {
      console.log('⚠️ OrderBy failed for chat history, trying simple query:', orderError.message);
      // Fallback to simple query without orderBy
      const chatsQuery = query(
        collection(db, 'chats'),
        where('userId', '==', userId)
      );
      snapshot = await getDocs(chatsQuery);
    }
    
    const chats = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Successfully loaded chat history:', chats.length, 'chats');
    return chats;
  } catch (error) {
    console.error('❌ Error getting user chat history:', error);
    return [];
  }
};

// Delete user chat
export const deleteUserChat = async (chatId, userId) => {
  try {
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    
    if (!chatSnap.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatSnap.data();
    if (chatData.userId !== userId) {
      throw new Error('Unauthorized to delete this chat');
    }
    
    await deleteDoc(chatRef);
    toast.success('Chat deleted successfully');
    return { success: true };
  } catch (error) {
    console.error('Error deleting chat:', error);
    toast.error('Failed to delete chat');
    return { success: false, error: error.message };
  }
};

// Clear all user chats
export const clearUserChatHistory = async (userId) => {
  try {
    const chatsQuery = query(
      collection(db, 'chats'),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(chatsQuery);
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    
    await Promise.all(deletePromises);
    toast.success('Chat history cleared successfully');
    return { success: true };
  } catch (error) {
    console.error('Error clearing chat history:', error);
    toast.error('Failed to clear chat history');
    return { success: false, error: error.message };
  }
};

// Get current user data
export const getCurrentUserData = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    
    console.log('🔍 Getting current user data for:', user.uid);
    
    const userRef = doc(db, 'users', user.uid);
    
    try {
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        console.log('✅ User document found in Firestore');
        return { id: user.uid, ...userSnap.data() };
      } else {
        console.log('⚠️ User document not found in Firestore, creating it...');
        // If user document doesn't exist, create it
        const createResult = await createUserDocument(user);
        
        if (createResult.success) {
          // Try to get it again
          const newUserSnap = await getDoc(userRef);
          if (newUserSnap.exists()) {
            console.log('✅ User document created and retrieved successfully');
            return { id: user.uid, ...newUserSnap.data() };
          }
        }
      }
    } catch (permissionError) {
      console.error('❌ Permission error accessing user document:', permissionError);
      
      // If we get a permission error, try to create the document with basic auth info
      console.log('🔧 Attempting to create user document due to permission error...');
      
      try {
        // Create user document with setDoc (which has different permission requirements)
        const basicUserData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          emailVerified: user.emailVerified || false,
          provider: user.providerData?.[0]?.providerId || 'email',
          createdAt: new Date(),
          lastLogin: new Date(),
          chatCount: 0,
          company: '',
          phone: '',
          location: '',
          designation: 'Other',
          updatedAt: new Date()
        };
        
        await setDoc(userRef, basicUserData);
        console.log('✅ User document created successfully with setDoc');
        
        // Return the created data
        return { id: user.uid, ...basicUserData };
        
      } catch (createError) {
        console.error('❌ Failed to create user document:', createError);
        
        // As a last resort, return basic user info from Firebase Auth
        console.log('⚠️ Returning basic user info from Firebase Auth');
        return {
          id: user.uid,
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          emailVerified: user.emailVerified || false,
          provider: user.providerData?.[0]?.providerId || 'email',
          createdAt: new Date(),
          lastLogin: new Date(),
          chatCount: 0,
          company: '',
          phone: '',
          location: '',
          designation: 'Other'
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error getting current user data:', error);
    
    // Return basic user info if available
    const user = auth.currentUser;
    if (user) {
      console.log('⚠️ Returning fallback user data from Firebase Auth');
      return {
        id: user.uid,
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        emailVerified: user.emailVerified || false,
        provider: user.providerData?.[0]?.providerId || 'email',
        createdAt: new Date(),
        lastLogin: new Date(),
        chatCount: 0,
        company: '',
        phone: '',
        location: '',
        designation: 'Other'
      };
    }
    
    return null;
  }
};

// Admin function to get all users
export const getAllUsers = async () => {
  try {
    console.log('🔍 Fetching all users from Firestore...');
    
    // Log user data access
    await logAuthEvent('user_data_access', 'Admin accessed all users data');
    
    // Try with orderBy first, fallback to simple query if it fails
    let snapshot;
    try {
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      snapshot = await getDocs(usersQuery);
    } catch (orderError) {
      console.log('⚠️ OrderBy failed, trying simple query:', orderError.message);
      // Fallback to simple query without orderBy
      snapshot = await getDocs(collection(db, 'users'));
    }
    
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Log successful user data retrieval
    await logAuthEvent('user_data_retrieved', `Retrieved ${users.length} user records`);
    
    console.log('✅ Successfully fetched users:', users.length);
    return users;
  } catch (error) {
    console.error('❌ Error getting all users:', error);
    return [];
  }
};

// Force create user document for current user (for debugging/fixing missing users)
export const forceCreateCurrentUserDocument = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }
    
    console.log('🔧 Force creating user document for:', user.uid);
    
    // Use direct setDoc to bypass any permission issues
    const userRef = doc(db, 'users', user.uid);
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      emailVerified: user.emailVerified || false,
      provider: user.providerData?.[0]?.providerId || 'email',
      createdAt: new Date(),
      lastLogin: new Date(),
      chatCount: 0,
      company: '',
      phone: '',
      location: '',
      designation: 'Other',
      updatedAt: new Date()
    };
    
    await setDoc(userRef, userData, { merge: true });
    console.log('✅ User document force created successfully');
    
    // Verify creation
    try {
      const verifySnap = await getDoc(userRef);
      if (verifySnap.exists()) {
        console.log('✅ Verification: User document exists and is accessible');
        return { success: true, userData: verifySnap.data() };
      } else {
        console.log('⚠️ Document created but not immediately readable');
        return { success: true, userData };
      }
    } catch (verifyError) {
      console.log('⚠️ Document created but verification failed:', verifyError.message);
      return { success: true, userData };
    }
    
  } catch (error) {
    console.error('❌ Error force creating user document:', error);
    return { success: false, error: error.message };
  }
};

// Comprehensive user sync function for debugging
export const ensureUserDocumentExists = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No user is currently signed in' };
    }
    
    console.log('🔄 Ensuring user document exists for:', user.uid);
    
    // Try multiple approaches to create/verify user document
    const approaches = [
      // Approach 1: Try normal createUserDocument
      async () => {
        console.log('📝 Approach 1: Normal createUserDocument');
        return await createUserDocument(user);
      },
      
      // Approach 2: Direct setDoc
      async () => {
        console.log('📝 Approach 2: Direct setDoc');
        const userRef = doc(db, 'users', user.uid);
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          emailVerified: user.emailVerified || false,
          provider: user.providerData?.[0]?.providerId || 'email',
          createdAt: new Date(),
          lastLogin: new Date(),
          chatCount: 0,
          company: '',
          phone: '',
          location: '',
          designation: 'Other',
          updatedAt: new Date()
        };
        
        await setDoc(userRef, userData, { merge: true });
        return { success: true, userData };
      },
      
      // Approach 3: Force create
      async () => {
        console.log('📝 Approach 3: Force create');
        return await forceCreateCurrentUserDocument();
      }
    ];
    
    for (let i = 0; i < approaches.length; i++) {
      try {
        const result = await approaches[i]();
        if (result.success) {
          console.log(`✅ Approach ${i + 1} succeeded`);
          return result;
        }
      } catch (error) {
        console.log(`❌ Approach ${i + 1} failed:`, error.message);
        if (i === approaches.length - 1) {
          throw error;
        }
      }
    }
    
    return { success: false, error: 'All approaches failed' };
    
  } catch (error) {
    console.error('❌ Error ensuring user document exists:', error);
    return { success: false, error: error.message };
  }
};

// Admin function to sync all Firebase Auth users to Firestore
export const syncAuthUsersToFirestore = async () => {
  try {
    console.log('🔄 Starting sync of Firebase Auth users to Firestore...');
    
    // This function would need Firebase Admin SDK to list all users
    // For now, we'll provide a manual approach
    console.log('⚠️ This function requires Firebase Admin SDK for full implementation');
    console.log('💡 Alternative: Users will be synced automatically when they sign in');
    
    return { 
      success: true, 
      message: 'Users will be synced automatically when they sign in. Encourage users to sign in again to appear in admin panel.' 
    };
  } catch (error) {
    console.error('❌ Error syncing users:', error);
    return { success: false, error: error.message };
  }
};

// Utility function to get object differences
const getObjectDifferences = (oldObj, newObj) => {
  const differences = {};
  
  for (const key in newObj) {
    if (oldObj[key] !== newObj[key]) {
      differences[key] = {
        old: oldObj[key],
        new: newObj[key]
      };
    }
  }
  
  return differences;
};

export { auth }; 